import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import './InvoiceSettingsModal.scss'; // Reuse styles as they are similar

interface ManualLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoiceId: string;
    onLinkSuccess: () => void;
}


interface BasicRFQ {
    id: string;
    // Modified to match DocumentItem structure loosely or use any
    [key: string]: any;
}
import documentApi from '../../../services/documentApi'; // Added import


const ManualLinkModal: React.FC<ManualLinkModalProps> = ({ isOpen, onClose, invoiceId, onLinkSuccess }) => {
    const { token } = useAuth();
    const [rfqs, setRfqs] = useState<BasicRFQ[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRfqId, setSelectedRfqId] = useState<string>('');
    const [linking, setLinking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch RFQs (Quotes) when opened
    useEffect(() => {
        if (isOpen && token) {
            fetchRfqs();
        }
    }, [isOpen, token]);

    const fetchRfqs = async () => {
        setIsLoading(true);
        try {
            // Using documentApi to fetch quotes
            const res = await documentApi.getDocuments(1, 100, 'quote');
            if (res.success && res.data) {
                setRfqs(res.data.documents);
            }
        } catch (err) {
            console.error('Failed to fetch Quotes', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLink = async () => {
        if (!selectedRfqId) return;

        setLinking(true);
        setError(null);
        try {
            // Fix: remove double /invoices prefix. Backend is /api/invoices/<id>/link-quote
            const res = await fetch(`/api/invoices/${invoiceId}/link-quote`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ quote_id: selectedRfqId })
            });

            const result = await res.json();
            if (result.success) {
                onLinkSuccess();
                onClose();
            } else {
                setError(result.message || 'Linking failed');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setLinking(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="invoice-settings-modal-overlay">
            <div className="invoice-settings-modal" style={{ width: '600px' }}>
                <div className="modal-header">
                    <h2>Manual Link to Quote</h2>
                    <button onClick={onClose} className="close-btn">&times;</button>
                </div>

                <div className="modal-body">
                    <p className="help-text">Select an uploaded Quote document to link with this invoice. This will trigger a re-verification.</p>

                    {isLoading ? <p>Loading Quotes...</p> : (
                        <div className="rfq-list" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                            {rfqs.map(rfq => (
                                <div
                                    key={rfq.id}
                                    className={`rfq-item ${selectedRfqId === rfq.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedRfqId(rfq.id)}
                                    style={{
                                        padding: '10px',
                                        borderBottom: '1px solid #f3f4f6',
                                        cursor: 'pointer',
                                        backgroundColor: selectedRfqId === rfq.id ? '#eff6ff' : 'white'
                                    }}
                                >
                                    <div style={{ fontWeight: 500 }}>{rfq.filename || 'Unnamed Document'}</div>
                                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                        Uploaded: {new Date(rfq.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            ))}
                            {rfqs.length === 0 && <div style={{ padding: '20px', textAlign: 'center' }}>No quotes found.</div>}
                        </div>
                    )}

                    {error && <div className="message error">{error}</div>}
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} disabled={linking}>Cancel</button>
                    <button
                        onClick={handleLink}
                        className="save-btn"
                        disabled={linking || !selectedRfqId}
                    >
                        {linking ? 'Linking & Verifying...' : 'Link Invoice'}
                    </button>
                </div>
            </div >
        </div >
    );
};

export default ManualLinkModal;
